import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { IMAGES, COMPANY } from "../../data/site";
import { scrollToSection } from "../../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Construction site with tower cranes against the London skyline"
          className="h-[120%] w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div className="absolute inset-0 bg-white/70" />
      <div className="absolute inset-0 bg-gradient-to-t from-white via-white/25 to-white/40" />
      <div className="bg-noise pointer-events-none absolute inset-0 opacity-[0.05]" />

      <div className="pointer-events-none absolute inset-0 hidden lg:block">
        <div className="mx-auto grid h-full max-w-7xl grid-cols-4 px-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="border-l border-black/[0.05]" />
          ))}
        </div>
      </div>

      <motion.svg
        viewBox="0 0 400 300"
        fill="none"
        initial="hidden"
        animate="visible"
        aria-hidden="true"
        className="pointer-events-none absolute -right-8 top-1/2 hidden w-[540px] -translate-y-1/2 opacity-60 xl:block"
      >
        {[
          "M40 160 L200 40 L360 160",
          "M70 160 L70 280 L330 280 L330 160",
          "M280 105 L280 60 L305 60 L305 124",
          "M180 280 L180 210 L230 210 L230 280",
          "M105 190 h40 v40 h-40 z",
          "M20 280 H380",
        ].map((d, i) => (
          <motion.path
            key={d}
            d={d}
            stroke="#AE8626"
            strokeWidth="2"
            variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1 } }}
            transition={{ duration: 1.4, delay: 1.2 + i * 0.15, ease: "easeInOut" }}
          />
        ))}
        <motion.path
          d="M20 292 H380 M20 286 v12 M380 286 v12"
          stroke="#AE8626"
          strokeWidth="1.5"
          strokeDasharray="5 6"
          variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
          transition={{ duration: 0.8, delay: 2.4 }}
        />
      </motion.svg>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-44 pt-40"
      >
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="h-[3px] w-12 bg-bronze" />
          <span className="text-xs font-medium uppercase tracking-[0.4em] text-black/60">
            19+ Years' Experience — Hounslow, London &amp; UK-Wide
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-[clamp(2.75rem,7.2vw,6.75rem)] font-extrabold uppercase leading-[0.95] tracking-tight text-ink"
        >
          <MaskedLine delay={0.35}>Building Quality,</MaskedLine>
          <MaskedLine delay={0.47}>
            Delivering <span className="text-gold-shine">Trust</span>
          </MaskedLine>
          <MaskedLine delay={0.59}>Across London</MaskedLine>
          <MaskedLine delay={0.71}>
            <span className="text-outline">&amp; The UK.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-fog md:text-lg"
        >
          A principal contractor registered in England &amp; Wales (No.{" "}
          {COMPANY.number}) — delivering residential, commercial and structural
          projects to the highest standard. On time, on budget, without
          compromise.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-quote"
            onClick={() => scrollToSection("#contact")}
            className="group relative overflow-hidden bg-bronze px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bronze"
          >
            Get a Quote
          </button>
          <button
            data-testid="hero-cta-projects"
            onClick={() => scrollToSection("#projects")}
            className="border border-black/30 bg-white/40 px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink backdrop-blur-sm transition-colors duration-300 hover:border-bronze hover:text-bronze"
          >
            View Projects
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute inset-x-0 bottom-0 z-10 border-t border-black/10 bg-white/70 backdrop-blur-sm"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div className="hidden items-center gap-8 text-[11px] uppercase tracking-[0.25em] text-black/50 md:flex">
            <span data-testid="hero-company-number">Company No. {COMPANY.number}</span>
            <span className="h-3 w-px bg-black/20" />
            <span>Registered in England &amp; Wales</span>
            <span className="h-3 w-px bg-black/20" />
            <span>Free Quotes</span>
          </div>
          <button
            data-testid="hero-scroll-cue"
            onClick={() => scrollToSection("#about")}
            className="flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-black/50 transition-colors duration-300 hover:text-bronze"
          >
            Scroll
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown className="h-4 w-4 text-bronze" />
            </motion.span>
          </button>
        </div>
      </motion.div>
    </section>
  );
};
